import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import portalSettingsManager from "utils/portalSettingsManager";

const SkipLinkAnchor = styled("a", {
  shouldForwardProp: (prop) => prop !== "backColor" && prop !== "textColor",
})<{ backColor: string; textColor: string }>(({ backColor, textColor, theme }) => ({
  position: "absolute",
  top: "-100px",
  left: "8px",
  zIndex: theme.zIndex.tooltip + 1,
  padding: "10px 16px",
  backgroundColor: backColor,
  color: textColor,
  textDecoration: "none",
  borderRadius: "4px",
  fontSize: "14px",
  fontWeight: "bold",
  transition: "top 0.2s ease-in-out",
  "&:focus": {
    top: "8px",
    outline: `2px solid ${textColor}`,
    outlineOffset: "2px",
  },
}));

type SkipLinkProps = {
  targetId?: string;
  backColor?: string;
  textColor?: string;
};

// Keyboard-accessible link that lets users bypass repeated header/nav and jump to the main content region.
export const SkipLink = ({
  targetId = "main-content",
  backColor = portalSettingsManager.header?.colors?.base?.back || "#FFFFFF",
  textColor = portalSettingsManager.header?.colors?.base?.text || "#000000",
}: SkipLinkProps): React.ReactElement => {
  const { t } = useTranslation();

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(targetId);
    if (!target) return;
    event.preventDefault();
    target.focus();
  };

  return (
    <SkipLinkAnchor
      href={`#${targetId}`}
      onClick={handleClick}
      backColor={backColor}
      textColor={textColor}
    >
      {t("skip_to_main_content")}
    </SkipLinkAnchor>
  );
};
